import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import MyLocationIcon from "@material-ui/icons/MyLocation";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles(theme => ({
  currentLocationButton: {
    marginBottom: 18,
  },
  preview: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 18,
    padding: 14,
    borderRadius: 12,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(59, 130, 246, 0.12)"
        : "rgba(37, 99, 235, 0.07)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(96, 165, 250, 0.25)"
        : "1px solid rgba(37, 99, 235, 0.16)",
  },
  previewIcon: {
    color: theme.palette.primary.main,
    fontSize: 34,
    flexShrink: 0,
  },
  previewText: {
    minWidth: 0,
    flex: 1,
  },
}));

const LocationModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [locating, setLocating] = useState(false);
  const [sending, setSending] = useState(false);

  const numericLatitude = Number(latitude);
  const numericLongitude = Number(longitude);
  const validCoordinates =
    latitude !== "" &&
    longitude !== "" &&
    Number.isFinite(numericLatitude) &&
    Number.isFinite(numericLongitude) &&
    numericLatitude >= -90 &&
    numericLatitude <= 90 &&
    numericLongitude >= -180 &&
    numericLongitude <= 180;

  const mapsUrl = useMemo(
    () =>
      validCoordinates
        ? `https://maps.google.com/?q=${numericLatitude},${numericLongitude}`
        : "",
    [validCoordinates, numericLatitude, numericLongitude]
  );

  const reset = () => {
    setLatitude("");
    setLongitude("");
    setName("");
    setAddress("");
  };

  const handleClose = () => {
    if (sending || locating) return;
    reset();
    onClose();
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(i18n.t("messagesInput.locationUnavailable"));
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      position => {
        setLatitude(position.coords.latitude.toFixed(6));
        setLongitude(position.coords.longitude.toFixed(6));
        setLocating(false);
      },
      () => {
        toast.error(i18n.t("messagesInput.locationPermissionError"));
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const handleSend = async () => {
    if (!validCoordinates) return;

    setSending(true);
    try {
      await api.post(`/messages/${ticketId}/location`, {
        latitude: numericLatitude,
        longitude: numericLongitude,
        name: name.trim(),
        address: address.trim(),
      });
      toast.success(i18n.t("messagesInput.locationSent"));
      reset();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("messagesInput.locationTitle")}</DialogTitle>
      <DialogContent dividers>
        <Button
          className={classes.currentLocationButton}
          variant="outlined"
          color="primary"
          startIcon={<MyLocationIcon />}
          onClick={handleUseCurrentLocation}
          disabled={locating || sending}
        >
          {locating
            ? i18n.t("messagesInput.locationLocating")
            : i18n.t("messagesInput.locationUseCurrent")}
        </Button>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              inputProps={{ step: "any", min: -90, max: 90 }}
              variant="outlined"
              label={i18n.t("messagesInput.locationLatitude")}
              value={latitude}
              onChange={event => setLatitude(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              inputProps={{ step: "any", min: -180, max: 180 }}
              variant="outlined"
              label={i18n.t("messagesInput.locationLongitude")}
              value={longitude}
              onChange={event => setLongitude(event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label={i18n.t("messagesInput.locationName")}
              value={name}
              onChange={event => setName(event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label={i18n.t("messagesInput.locationAddress")}
              value={address}
              onChange={event => setAddress(event.target.value)}
              helperText={i18n.t("messagesInput.locationHelp")}
            />
          </Grid>
        </Grid>

        {validCoordinates && (
          <div className={classes.preview}>
            <LocationOnIcon className={classes.previewIcon} />
            <div className={classes.previewText}>
              <Typography variant="subtitle2">
                {name.trim() || i18n.t("messagesInput.locationPreview")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {address.trim() || `${latitude}, ${longitude}`}
              </Typography>
            </div>
            <Button
              color="primary"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(mapsUrl, "_blank", "noopener,noreferrer")}
            >
              {i18n.t("messagesInput.locationOpenMap")}
            </Button>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          variant="outlined"
          disabled={sending || locating}
        >
          {i18n.t("messagesInput.locationCancel")}
        </Button>
        <ButtonWithSpinner
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={!validCoordinates || sending || locating}
          loading={sending}
        >
          {i18n.t("messagesInput.locationSend")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default LocationModal;
